// client/src/pages/Financing/FinancingPage.jsx
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  CheckCircle, ArrowRight, ChevronDown,
  Shield, Briefcase, TrendingUp, CreditCard,
  Calculator, Phone, GraduationCap, ArrowLeft,
  DollarSign, Award, Users,
} from 'lucide-react';

// ── Programs module palette (exact match) ──────────────────────────────────
const C = {
  navy:     '#1B2B4B',
  navyDark: '#101D33',
  navyLight:'#243860',
  silver:   '#A8B8C8',   // accent (was gold)
  silverLight:'#FFFFFF',
  silverPale: '#EEF3FA', // card / tag bg (was goldPale)
  white:    '#FFFFFF',
  offWhite: '#EBF0F8',   // light section bg (was F8F7F4)
  lightGray:'#D8E6F2',
  darkGray: '#3A4A60',
  textMuted:'#5A6880',
  border:   'rgba(27,43,75,0.14)',
  green:    '#16A34A',
  dark1:    '#071628',   // deepest navy (was #1A1A1A)
  dark2:    '#0d1e38',   // dark navy (was #262523)
  dark3:    '#0a1830',
};

const U = "'Urbanist', sans-serif";

const globalStyles = `
@import url('https://fonts.googleapis.com/css2?family=Urbanist:wght@300;400;500;600;700;800;900&display=swap');
*, *::before, *::after { box-sizing: border-box; }

.fp-section    { padding: 100px 48px; }
.fp-section-sm { padding: 80px 48px; }
.fp-inner      { max-width: 1200px; margin: 0 auto; }
.fp-inner-md   { max-width: 900px; margin: 0 auto; }
.fp-inner-sm   { max-width: 800px; margin: 0 auto; }

.fp-hero {
  position: relative; min-height: 60vh;
  display: flex; align-items: center;
  overflow: hidden; background: #071628;
  padding: 140px 48px 100px;
}
.fp-hero-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
}
.fp-hero-btns { display: flex; gap: 14px; flex-wrap: wrap; }
.fp-tiers { display: flex; gap: 12px; margin-bottom: 32px; }
.fp-tier  { flex: 1; }
.fp-stats { display: flex; gap: 0; }
.fp-stat  { flex: 1; }

.fp-calc-step-btns { display: flex; gap: 10px; }
.fp-result-actions { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 8px; }

.fp-options-list { display: flex; flex-direction: column; gap: 2px; }
.fp-option-card  {
  background: #fff;
  border: 1px solid rgba(27,43,75,0.14);
  border-left: 4px solid #A8B8C8;
  border-radius: 4px;
  padding: 32px 36px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: start;
  transition: box-shadow 0.2s;
}
.fp-option-card:hover { box-shadow: 0 6px 24px rgba(27,43,75,0.10); }

.fp-faq-list { display: flex; flex-direction: column; gap: 2px; }

@media (max-width: 1024px) {
  .fp-section    { padding: 80px 32px; }
  .fp-section-sm { padding: 60px 32px; }
  .fp-hero       { padding: 120px 32px 80px; }
  .fp-hero-grid  { grid-template-columns: 1fr; gap: 48px; }
  .fp-option-card { grid-template-columns: 1fr; gap: 28px; }
}
@media (max-width: 768px) {
  .fp-section    { padding: 60px 20px; }
  .fp-section-sm { padding: 48px 20px; }
  .fp-hero       { padding: 100px 20px 60px; min-height: unset; }
  .fp-hero-grid  { grid-template-columns: 1fr; gap: 36px; }
  .fp-option-card { grid-template-columns: 1fr; gap: 24px; padding: 24px 20px; }
  .fp-tiers { flex-direction: column; }
  .fp-stats { flex-wrap: wrap; }
  .fp-stat  { flex: 1 1 45%; padding-right: 0 !important; border-right: none !important; border-bottom: 1px solid rgba(255,255,255,0.08); padding-bottom: 12px; margin-bottom: 4px; text-align: left !important; }
  .fp-hero-btns { flex-direction: column; }
  .fp-hero-btns a { width: 100%; text-align: center; justify-content: center; }
  .fp-calc-step-btns { flex-direction: column; }
  .fp-result-actions { flex-direction: column; }
  .fp-result-actions a { width: 100%; justify-content: center; }
}
@media (max-width: 420px) {
  .fp-stat { flex: 1 1 100%; }
}
`;

// ── Data ──────────────────────────────────────────────────────────────────────
const TUITION = { regular: 7999, veteran: 4999 };
const PLAN_MONTHS = [3, 6, 12];

const FINANCING = [
  { id: 'self-pay',     label: 'Self-Pay',          icon: CreditCard, color: C.navy,    coverage: 0,   desc: 'Pay tuition in full or via our 0% interest payment plan.', highlight: false },
  { id: 'payment-plan', label: 'Payment Plan',       icon: Calculator, color: '#2563EB', coverage: 0,   desc: 'Split tuition into 3, 6, or 12 monthly installments at 0% interest.', highlight: false },
  { id: 'gi-bill',      label: 'Post-9/11 GI Bill',  icon: Shield,     color: '#DC2626', coverage: 100, desc: 'Covers up to 100% of tuition for qualifying veterans plus monthly housing allowance.', highlight: true },
  { id: 'wioa',         label: 'WIOA Grant',          icon: TrendingUp, color: '#7C3AED', coverage: 100, desc: 'Federal workforce grant for qualifying unemployed or underemployed applicants.', highlight: true },
  { id: 'employer',     label: 'Employer Sponsored',  icon: Briefcase,  color: C.silver,  coverage: 100, desc: 'Partner carriers pay full tuition in exchange for a hiring commitment after graduation.', highlight: true },
];

const financingOptions = [
  {
    icon: Shield, title: 'Post-9/11 GI Bill', tag: 'Veterans', tagColor: '#DC2626',
    coverage: 'Up to 100% tuition',
    desc: 'For veterans with 90+ days of active duty service after Sept. 10, 2001. Covers full tuition plus a monthly housing allowance during training.',
    steps: ['Apply at va.gov or visit a VA office', 'Our advisor handles all school certification paperwork', 'Benefits paid directly to CST on your behalf'],
  },
  {
    icon: TrendingUp, title: 'WIOA Grant', tag: 'Federal', tagColor: '#7C3AED',
    coverage: 'Full tuition',
    desc: 'Workforce Innovation and Opportunity Act grants for unemployed or underemployed individuals seeking career retraining in high-demand fields.',
    steps: ['Visit your local EDD or American Job Center', 'Get approved for an Individual Training Account (ITA)', 'CST handles the rest — zero paperwork for you'],
  },
  {
    icon: Briefcase, title: 'Employer Sponsorship', tag: 'Corporate', tagColor: C.navy,
    coverage: '100% tuition reimbursement',
    desc: 'Pre-hire agreements with Schneider, Swift Transportation, JB Hunt and other partner carriers. They pay your tuition in exchange for a hiring commitment.',
    steps: ['Apply to CST and indicate employer sponsorship interest', 'We match you with a carrier based on your location and goals', 'Carrier pays tuition — you start earning immediately after graduation'],
  },
  {
    icon: Calculator, title: 'Payment Plan', tag: '0% Interest', tagColor: '#2563EB',
    coverage: 'Split into 3, 6, or 12 months',
    desc: 'Our in-house 0% interest payment plan lets you split tuition into manageable monthly installments with no credit check required.',
    steps: ['Choose your plan length at enrollment (3, 6, or 12 months)', 'First payment due at orientation', 'Auto-pay available — never miss a payment'],
  },
  {
    icon: CreditCard, title: 'Self-Pay', tag: 'Full Payment', tagColor: C.navy,
    coverage: 'One-time payment',
    desc: 'Pay tuition in full at enrollment. No monthly obligations, no interest, no financing paperwork. The simplest path to getting started.',
    steps: ['Pay in full at or before orientation', 'Accept all major credit cards, debit, check, or wire transfer', 'Receipt provided immediately — no waiting'],
  },
];

const faqs = [
  { q: 'When is tuition due?', a: 'Tuition is due in full by your first day of class, unless you are on an approved payment plan or have a confirmed funding source (GI Bill, WIOA, employer sponsorship).' },
  { q: 'Is there a registration or application fee?', a: 'There is no application fee. A refundable enrollment deposit may be required to hold your seat in select programs.' },
  { q: "What if my GI Bill doesn't cover the full amount?", a: 'We use a blended funding model. Our financial advisor will identify supplemental grants, state programs, or employer partnerships to cover any remaining balance.' },
  { q: 'Can I get a refund if I drop out?', a: 'Our refund policy follows California BPPE regulations. Refunds are prorated based on the percentage of the program completed at the time of withdrawal.' },
  { q: 'Do you offer scholarships?', a: 'Yes. CST offers merit-based and need-based scholarships for qualifying students. Ask your enrollment advisor about current scholarship availability.' },
  { q: 'How long does financing approval take?', a: 'Payment plan approval is immediate. GI Bill and WIOA processing typically takes 2–4 weeks — we recommend applying at least 30 days before your start date.' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function SectionLabel({ text, center = false }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, justifyContent: center ? 'center' : 'flex-start' }}>
      <div style={{ width: 32, height: 2, background: C.silver }} />
      <span style={{ fontFamily: U, fontSize: 12, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.silver }}>{text}</span>
      {center && <div style={{ width: 32, height: 2, background: C.silver }} />}
    </div>
  );
}

function StepIndicator({ currentStep, labels }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 28, overflowX: 'auto' }}>
      {labels.map((label, i) => {
        const stepNum = i + 1;
        const isCompleted = stepNum < currentStep;
        const isActive = stepNum === currentStep;
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', flex: i < labels.length - 1 ? 1 : 'none' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: isCompleted ? C.green : isActive ? C.silver : C.lightGray, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', flexShrink: 0 }}>
                {isCompleted
                  ? <CheckCircle size={16} color={C.white} />
                  : <span style={{ fontFamily: U, fontSize: 13, fontWeight: 700, color: isActive ? C.navyDark : C.darkGray }}>{stepNum}</span>}
              </div>
              <span style={{ fontFamily: U, fontSize: 10, fontWeight: 600, color: isActive ? C.silver : isCompleted ? C.green : C.darkGray, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{label}</span>
            </div>
            {i < labels.length - 1 && (
              <div style={{ flex: 1, height: 2, background: isCompleted ? C.green : C.lightGray, margin: '0 8px', marginBottom: 22, transition: 'background 0.3s', minWidth: 20 }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Tuition Calculator ────────────────────────────────────────────────────────
export function TuitionCalculator({ compact = false }) {
  const [step, setStep] = useState(1);
  const [isVeteran, setIsVeteran] = useState(null);
  const [selectedFinancing, setSelectedFinancing] = useState(null);
  const [planMonths, setPlanMonths] = useState(6);

  const stepLabels = selectedFinancing?.id === 'payment-plan'
    ? ['Student Type', 'Financing', 'Plan', 'Result']
    : ['Student Type', 'Financing', 'Result'];

  const tuition = isVeteran === null ? 0 : isVeteran ? TUITION.veteran : TUITION.regular;
  const coverage = selectedFinancing?.coverage || 0;
  const covered = Math.round((coverage / 100) * tuition);
  const outOfPocket = tuition - covered;
  const isFree = outOfPocket === 0 && selectedFinancing !== null;
  const monthlyPayment = selectedFinancing?.id === 'payment-plan' ? Math.ceil(outOfPocket / planMonths) : 0;
  const canNext = step === 1 ? isVeteran !== null : step === 2 ? !!selectedFinancing : true;
  const isLastStep = selectedFinancing?.id === 'payment-plan' ? step === 4 : step === 3;

  const handleNext = () => setStep(s => s + 1);
  const handleBack = () => setStep(s => s - 1);
  const handleReset = () => { setStep(1); setIsVeteran(null); setSelectedFinancing(null); setPlanMonths(6); };

  return (
    <div style={{ background: C.white, borderRadius: 4, border: `1px solid ${C.border}`, borderTop: `4px solid ${C.silver}`, overflow: 'hidden', boxShadow: '0 8px 40px rgba(7,22,40,0.18)' }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${C.dark1} 0%, ${C.dark2} 100%)`, padding: compact ? '20px 24px' : '28px 36px', borderBottom: `1px solid rgba(168,184,200,0.15)` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Calculator size={16} color={C.silver} />
          <span style={{ fontFamily: U, fontSize: 11, fontWeight: 600, letterSpacing: '0.10em', textTransform: 'uppercase', color: C.silver }}>Tuition Calculator</span>
        </div>
        <h3 style={{ fontFamily: U, fontSize: compact ? 20 : 26, fontWeight: 800, color: C.white, margin: 0 }}>Find Your Cost in Seconds</h3>
        <p style={{ fontFamily: U, fontSize: 14, fontWeight: 300, color: 'rgba(255,255,255,0.55)', margin: '6px 0 0' }}>
          {step === 1 && 'Step 1 — Are you a veteran or military student?'}
          {step === 2 && 'Step 2 — How will you pay for training?'}
          {step === 3 && selectedFinancing?.id === 'payment-plan' && 'Step 3 — Choose your payment plan length.'}
          {((step === 3 && selectedFinancing?.id !== 'payment-plan') || step === 4) && "Here's your estimated cost breakdown."}
        </p>
      </div>

      <div style={{ padding: compact ? '24px' : '32px' }}>
        <StepIndicator currentStep={step} labels={stepLabels} />

        {/* STEP 1 */}
        {step === 1 && (
          <div style={{ marginBottom: 24 }}>
            <p style={{ fontFamily: U, fontSize: 13, fontWeight: 300, color: C.textMuted, marginBottom: 16, lineHeight: 1.6 }}>
              Select your student type to see your tuition rate.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button onClick={() => setIsVeteran(false)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px', border: `2px solid ${isVeteran === false ? C.silver : C.border}`, borderRadius: 4, background: isVeteran === false ? C.silverPale : C.white, cursor: 'pointer', transition: 'all 0.15s', textAlign: 'left', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  {isVeteran === false ? <CheckCircle size={20} color={C.silver} /> : <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${C.border}` }} />}
                  <div>
                    <p style={{ margin: 0, fontFamily: U, fontSize: 15, fontWeight: 700, color: isVeteran === false ? C.navy : C.darkGray }}>Regular Student</p>
                    <p style={{ margin: '2px 0 0', fontFamily: U, fontSize: 12, fontWeight: 300, color: C.textMuted }}>Standard enrollment rate</p>
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <p style={{ margin: 0, fontFamily: U, fontSize: 26, fontWeight: 800, color: isVeteran === false ? C.navy : C.darkGray }}>${TUITION.regular.toLocaleString()}</p>
                  <p style={{ margin: '2px 0 0', fontFamily: U, fontSize: 11, color: C.textMuted }}>full tuition</p>
                </div>
              </button>

              <button onClick={() => setIsVeteran(true)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px', border: `2px solid ${isVeteran === true ? '#DC2626' : C.border}`, borderRadius: 4, background: isVeteran === true ? 'rgba(220,38,38,0.05)' : C.white, cursor: 'pointer', transition: 'all 0.15s', textAlign: 'left', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  {isVeteran === true ? <CheckCircle size={20} color='#DC2626' /> : <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${C.border}` }} />}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <p style={{ margin: 0, fontFamily: U, fontSize: 15, fontWeight: 700, color: isVeteran === true ? '#DC2626' : C.darkGray }}>Veteran / Military</p>
                      <span style={{ background: '#DC2626', color: C.white, fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 20, fontFamily: U, letterSpacing: '0.06em' }}>SPECIAL RATE</span>
                    </div>
                    <p style={{ margin: '2px 0 0', fontFamily: U, fontSize: 12, fontWeight: 300, color: C.textMuted }}>Active military &amp; veterans</p>
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <p style={{ margin: 0, fontFamily: U, fontSize: 26, fontWeight: 800, color: isVeteran === true ? '#DC2626' : C.darkGray }}>${TUITION.veteran.toLocaleString()}</p>
                  <p style={{ margin: '2px 0 0', fontFamily: U, fontSize: 11, color: C.textMuted }}>full tuition</p>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {FINANCING.map((f) => {
                const Icon = f.icon;
                const active = selectedFinancing?.id === f.id;
                return (
                  <button key={f.id} onClick={() => setSelectedFinancing(f)} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', border: `2px solid ${active ? C.silver : C.border}`, borderRadius: 4, background: active ? C.silverPale : C.white, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s', width: '100%' }}>
                    <div style={{ width: 38, height: 38, borderRadius: 4, flexShrink: 0, background: active ? 'rgba(168,184,200,0.18)' : C.offWhite, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon size={17} color={active ? C.silver : f.color} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: active ? C.navy : C.darkGray, fontFamily: U }}>{f.label}</p>
                        {f.highlight && <span style={{ background: C.green, color: C.white, fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 20, fontFamily: U, letterSpacing: '0.06em', flexShrink: 0 }}>ZERO COST</span>}
                      </div>
                      <p style={{ margin: '2px 0 0', fontSize: 12, color: C.textMuted, fontFamily: U, fontWeight: 300 }}>{f.desc}</p>
                    </div>
                    {active && <CheckCircle size={18} color={C.silver} style={{ flexShrink: 0 }} />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 3 — Payment plan */}
        {step === 3 && selectedFinancing?.id === 'payment-plan' && (
          <div style={{ marginBottom: 24 }}>
            <p style={{ fontFamily: U, fontSize: 14, fontWeight: 300, color: C.textMuted, marginBottom: 16 }}>All plans are 0% interest · No credit check required.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {PLAN_MONTHS.map((m) => {
                const active = planMonths === m;
                const monthly = Math.ceil(tuition / m);
                return (
                  <button key={m} onClick={() => setPlanMonths(m)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', border: `2px solid ${active ? C.silver : C.border}`, borderRadius: 4, background: active ? C.silverPale : C.white, cursor: 'pointer', transition: 'all 0.15s', width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {active ? <CheckCircle size={18} color={C.silver} /> : <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${C.border}` }} />}
                      <div style={{ textAlign: 'left' }}>
                        <p style={{ margin: 0, fontFamily: U, fontSize: 15, fontWeight: 700, color: active ? C.navy : C.darkGray }}>{m} Month Plan</p>
                        <p style={{ margin: '2px 0 0', fontFamily: U, fontSize: 12, fontWeight: 300, color: C.textMuted }}>0% interest · No credit check</p>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <p style={{ margin: 0, fontFamily: U, fontSize: 22, fontWeight: 800, color: active ? C.navy : C.darkGray }}>${monthly.toLocaleString()}</p>
                      <p style={{ margin: '2px 0 0', fontFamily: U, fontSize: 11, color: C.textMuted }}>per month</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* RESULT */}
        {((step === 3 && selectedFinancing?.id !== 'payment-plan') || step === 4) && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, padding: '12px 16px', background: C.offWhite, borderRadius: 4, border: `1px solid ${C.border}`, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 80, textAlign: 'center', borderRight: `1px solid ${C.border}`, paddingRight: 10 }}>
                <p style={{ margin: 0, fontFamily: U, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.textMuted }}>Student Type</p>
                <p style={{ margin: '4px 0 0', fontFamily: U, fontSize: 13, fontWeight: 700, color: isVeteran ? '#DC2626' : C.navy }}>{isVeteran ? 'Veteran' : 'Regular'}</p>
              </div>
              <div style={{ flex: 1, minWidth: 80, textAlign: 'center', borderRight: selectedFinancing?.id === 'payment-plan' ? `1px solid ${C.border}` : 'none', paddingRight: selectedFinancing?.id === 'payment-plan' ? 10 : 0 }}>
                <p style={{ margin: 0, fontFamily: U, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.textMuted }}>Financing</p>
                <p style={{ margin: '4px 0 0', fontFamily: U, fontSize: 13, fontWeight: 700, color: C.navy }}>{selectedFinancing?.label}</p>
              </div>
              {selectedFinancing?.id === 'payment-plan' && (
                <div style={{ flex: 1, minWidth: 80, textAlign: 'center' }}>
                  <p style={{ margin: 0, fontFamily: U, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.textMuted }}>Plan</p>
                  <p style={{ margin: '4px 0 0', fontFamily: U, fontSize: 13, fontWeight: 700, color: C.navy }}>{planMonths} Months</p>
                </div>
              )}
            </div>

            <div style={{ background: isFree ? '#F0FDF4' : C.offWhite, border: `2px solid ${isFree ? C.green : C.border}`, borderRadius: 4, padding: '24px', marginBottom: 16 }}>
              {isFree ? (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                    <CheckCircle size={30} color={C.green} />
                  </div>
                  <p style={{ fontFamily: U, fontSize: 32, fontWeight: 800, color: C.green, margin: '0 0 6px' }}>$0 Out of Pocket</p>
                  <p style={{ fontFamily: U, fontSize: 14, fontWeight: 300, color: C.textMuted, margin: 0 }}>
                    {selectedFinancing?.label} covers 100% of your tuition (${tuition.toLocaleString()}).
                  </p>
                </div>
              ) : (
                <div>
                  <p style={{ fontFamily: U, fontSize: 11, fontWeight: 700, color: C.navy, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 16px' }}>Your Cost Breakdown</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[
                      { label: 'Tuition',          value: `$${tuition.toLocaleString()}`,               color: C.darkGray },
                      { label: 'Funding Coverage', value: covered > 0 ? `-$${covered.toLocaleString()}` : '$0', color: C.green },
                      { label: 'Your Out-of-Pocket', value: `$${outOfPocket.toLocaleString()}`,          color: C.navy, bold: true },
                      ...(selectedFinancing?.id === 'payment-plan'
                        ? [{ label: `Monthly (${planMonths} months)`, value: `$${monthlyPayment.toLocaleString()}/mo`, color: '#2563EB', bold: true }]
                        : []),
                    ].map(({ label, value, color, bold }) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 10, borderBottom: `1px solid ${C.border}` }}>
                        <span style={{ fontFamily: U, fontSize: 14, fontWeight: 300, color: C.textMuted }}>{label}</span>
                        <span style={{ fontFamily: U, fontSize: bold ? 20 : 16, fontWeight: bold ? 800 : 500, color }}>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="fp-result-actions">
              <a href="/apply" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px 24px', background: C.navy, color: C.white, borderRadius: 4, textDecoration: 'none', fontFamily: U, fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                <GraduationCap size={15} /> Apply Now
              </a>
              <a href="tel:18884982689" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px 24px', background: 'transparent', color: C.navy, border: `2px solid ${C.border}`, borderRadius: 4, textDecoration: 'none', fontFamily: U, fontSize: 13, fontWeight: 600 }}>
                <Phone size={15} /> Speak to Advisor
              </a>
            </div>
            <button onClick={handleReset} style={{ width: '100%', padding: '10px', background: 'none', border: 'none', cursor: 'pointer', fontFamily: U, fontSize: 13, fontWeight: 500, color: C.textMuted, textDecoration: 'underline' }}>Start Over</button>
          </div>
        )}

        {!isLastStep && (
          <div className="fp-calc-step-btns">
            {step > 1 && (
              <button onClick={handleBack} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '12px 20px', background: C.white, border: `2px solid ${C.border}`, borderRadius: 4, cursor: 'pointer', fontFamily: U, fontSize: 14, fontWeight: 600, color: C.darkGray }}>
                <ArrowLeft size={15} /> Back
              </button>
            )}
            <button onClick={handleNext} disabled={!canNext} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 24px', background: canNext ? C.silver : C.lightGray, border: 'none', borderRadius: 4, cursor: canNext ? 'pointer' : 'not-allowed', fontFamily: U, fontSize: 14, fontWeight: 700, color: canNext ? C.navyDark : C.darkGray, letterSpacing: '0.04em', transition: 'all 0.2s' }}>
              {step === 1 ? 'Choose Financing' : step === 2 && selectedFinancing?.id === 'payment-plan' ? 'Choose Plan Length' : 'See My Cost'}
              <ArrowRight size={15} />
            </button>
          </div>
        )}
        {!isLastStep && (
          <p style={{ fontFamily: U, fontSize: 11, fontWeight: 300, color: C.textMuted, textAlign: 'center', margin: '12px 0 0' }}>
            Tuition estimates are subject to enrollment review. Final pricing confirmed at orientation.
          </p>
        )}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function FinancingPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <>
      <Helmet>
        <title>CDL Financing Options | California School of Trucking</title>
        <meta name="description" content="Flexible CDL training financing. In-house payment plans, GI Bill, WIOA grants, and carrier-sponsored tuition reimbursement. Don't let cost stop you." />
        <meta property="og:title" content="CDL Financing Options | California School of Trucking" />
        <meta property="og:description" content="Flexible CDL training financing. In-house payment plans, GI Bill, WIOA grants, and carrier-sponsored tuition reimbursement. Don't let cost stop you." />
        <meta property="og:type" content="website" />
      </Helmet>
      <style>{globalStyles}</style>

      {/* ── SECTION 1: HERO (dark navy) ── */}
      <section className="fp-hero">
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `radial-gradient(circle at 75% 45%, rgba(168,184,200,0.07) 0%, transparent 40%), linear-gradient(135deg, ${C.dark1} 0%, ${C.dark2} 100%)`, zIndex: 0 }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)`, backgroundSize: '60px 60px', zIndex: 0 }} />
        <div style={{ position: 'absolute', left: 0, top: '28%', width: 4, height: 180, background: `linear-gradient(to bottom, transparent, ${C.silver}, transparent)`, zIndex: 1 }} />

        <div className="fp-inner" style={{ position: 'relative', zIndex: 2, width: '100%' }}>
          <div className="fp-hero-grid">
            {/* Left */}
            <div>
              <SectionLabel text="Tuition & Financing" />
              <h1 style={{ fontFamily: U, fontSize: 'clamp(30px, 4vw, 54px)', fontWeight: 900, color: C.white, lineHeight: 1.1, margin: '0 0 18px' }}>
                Your Career Shouldn&apos;t<br />
                <span style={{ color: C.silver }}>Cost You Everything.</span>
              </h1>
              <p style={{ fontFamily: U, fontSize: 'clamp(14px, 1.4vw, 17px)', fontWeight: 300, color: 'rgba(255,255,255,0.62)', lineHeight: 1.8, margin: '0 0 28px', maxWidth: 460 }}>
                Most of our students pay zero out of pocket. Between GI Bill benefits, federal grants, and employer sponsorships — we'll find a path that works for you.
              </p>

              {/* Tuition tiers */}
              <div className="fp-tiers">
                <div className="fp-tier" style={{ background: 'rgba(168,184,200,0.08)', border: '1px solid rgba(168,184,200,0.22)', borderRadius: 4, padding: '16px', textAlign: 'center' }}>
                  <div style={{ fontFamily: U, fontSize: 10, fontWeight: 700, color: C.silver, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Regular Students</div>
                  <div style={{ fontFamily: U, fontSize: 28, fontWeight: 800, color: C.white }}>${TUITION.regular.toLocaleString()}</div>
                </div>
                <div className="fp-tier" style={{ background: 'rgba(220,38,38,0.07)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 4, padding: '16px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, marginBottom: 6 }}>
                    <Shield size={10} color='#DC2626' />
                    <div style={{ fontFamily: U, fontSize: 10, fontWeight: 700, color: '#DC2626', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Veterans / Military</div>
                  </div>
                  <div style={{ fontFamily: U, fontSize: 28, fontWeight: 800, color: C.white }}>${TUITION.veteran.toLocaleString()}</div>
                </div>
              </div>

              {/* Stats */}
              <div className="fp-stats">
                {[
                  { value: '$0',   label: 'For qualifying students' },
                  { value: '100%', label: 'Tuition coverage options' },
                  { value: '5+',   label: 'Financing pathways' },
                ].map((s, i) => (
                  <div key={i} className="fp-stat" style={{ textAlign: 'center', paddingRight: i < 2 ? 20 : 0, borderRight: i < 2 ? '1px solid rgba(168,184,200,0.15)' : 'none' }}>
                    <div style={{ fontFamily: U, fontSize: 30, fontWeight: 900, color: C.silver }}>{s.value}</div>
                    <div style={{ fontFamily: U, fontSize: 11, fontWeight: 400, color: 'rgba(255,255,255,0.35)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Calculator */}
            <TuitionCalculator compact />
          </div>
        </div>
      </section>

      {/* ── SECTION 2: STATS BAR (navy) ── */}
      <section style={{ background: C.navy, padding: '0 48px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', borderLeft: `1px solid rgba(168,184,200,0.15)` }}>
          {[
            { value: 'GI Bill',    label: 'VA-Approved School',      icon: Shield     },
            { value: 'WIOA',       label: 'Federal Grant Eligible',   icon: Award      },
            { value: '$0',         label: 'For Qualifying Students',  icon: DollarSign },
            { value: '5 Options',  label: 'Financing Pathways',       icon: Users      },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} style={{ padding: '32px 28px', borderRight: `1px solid rgba(168,184,200,0.15)`, display: 'flex', alignItems: 'center', gap: 16 }}>
                <Icon size={24} color={C.silver} style={{ flexShrink: 0 }} />
                <div>
                  <div style={{ fontFamily: U, fontSize: 24, fontWeight: 800, color: C.white, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontFamily: U, fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.40)', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 4 }}>{s.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── SECTION 3: FINANCING OPTIONS (light — blue-tinted white) ── */}
      <section className="fp-section" style={{ background: C.offWhite }}>
        <div className="fp-inner">
          <div style={{ marginBottom: 56 }}>
            <SectionLabel text="Financing Options" />
            <h2 style={{ fontFamily: U, fontSize: 'clamp(24px, 3.5vw, 42px)', fontWeight: 800, color: C.navy, margin: '0 0 14px' }}>
              Five Ways to Fund Your Training
            </h2>
            <p style={{ fontFamily: U, fontSize: 'clamp(14px,1.4vw,16px)', fontWeight: 300, color: C.textMuted, maxWidth: 520, lineHeight: 1.75, margin: 0 }}>
              Our financial advisors work with every student to identify the best combination of funding sources for their situation.
            </p>
          </div>

          <div className="fp-options-list">
            {financingOptions.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="fp-option-card">
                  {/* Left */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 4, background: C.lightGray, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon size={20} color={C.navy} />
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <h3 style={{ fontFamily: U, fontSize: 18, fontWeight: 800, color: C.navy, margin: 0 }}>{f.title}</h3>
                          <span style={{ background: C.silverPale, color: f.tagColor, fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 20, fontFamily: U, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{f.tag}</span>
                        </div>
                        <p style={{ margin: '3px 0 0', fontFamily: U, fontSize: 13, fontWeight: 600, color: C.silver }}>{f.coverage}</p>
                      </div>
                    </div>
                    <p style={{ fontFamily: U, fontSize: 14, fontWeight: 300, color: C.textMuted, lineHeight: 1.75, margin: 0 }}>{f.desc}</p>
                  </div>

                  {/* Right — Steps */}
                  <div>
                    <p style={{ fontFamily: U, fontSize: 11, fontWeight: 700, color: C.navy, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 14px' }}>How It Works</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {f.steps.map((step, si) => (
                        <div key={si} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                          <div style={{ width: 22, height: 22, borderRadius: '50%', background: C.silverPale, border: `1px solid rgba(168,184,200,0.35)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                            <span style={{ fontFamily: U, fontSize: 10, fontWeight: 700, color: C.navy }}>{si + 1}</span>
                          </div>
                          <p style={{ fontFamily: U, fontSize: 13, fontWeight: 300, color: C.darkGray, lineHeight: 1.6, margin: 0 }}>{step}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── SECTION 4: FAQ (dark — deep navy) ── */}
      <section className="fp-section" style={{ background: C.dark2, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `radial-gradient(circle at 20% 50%, rgba(168,184,200,0.05) 0%, transparent 42%)`, zIndex: 0 }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(rgba(255,255,255,0.018) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.018) 1px, transparent 1px)`, backgroundSize: '60px 60px', zIndex: 0 }} />
        <div className="fp-inner-sm" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <SectionLabel text="Frequently Asked Questions" center />
            <h2 style={{ fontFamily: U, fontSize: 'clamp(24px, 3.5vw, 42px)', fontWeight: 800, color: C.white, margin: 0 }}>Financing FAQ</h2>
          </div>
          <div className="fp-faq-list">
            {faqs.map((f, i) => (
              <div key={i} style={{ background: C.dark1, border: '1px solid rgba(168,184,200,0.12)', borderRadius: 4, overflow: 'hidden', marginBottom: 2 }}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'clamp(14px,2vw,20px) clamp(16px,2.5vw,28px)', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: 16 }}>
                  <span style={{ fontFamily: U, fontSize: 'clamp(13px,1.3vw,15px)', fontWeight: 600, color: C.white }}>{f.q}</span>
                  <ChevronDown size={17} color={C.silver} style={{ transform: openFaq === i ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
                </button>
                {openFaq === i && (
                  <div style={{ padding: '0 clamp(16px,2.5vw,28px) 20px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    <p style={{ fontFamily: U, fontSize: 'clamp(13px,1.2vw,14px)', fontWeight: 300, color: 'rgba(255,255,255,0.58)', lineHeight: 1.8, margin: '16px 0 0' }}>{f.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 5: CTA (light — blue-tinted white) ── */}
      <section className="fp-section" style={{ background: C.offWhite, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: `linear-gradient(to bottom, transparent, ${C.silver}, transparent)` }} />
        <div className="fp-inner-md" style={{ textAlign: 'center', position: 'relative' }}>
          <SectionLabel text="Talk to a Financial Advisor" center />
          <h2 style={{ fontFamily: U, fontSize: 'clamp(24px, 4vw, 46px)', fontWeight: 900, color: C.navy, margin: '0 0 16px', lineHeight: 1.2 }}>
            Not Sure Which Option<br />
            <span style={{ color: C.silver }}>Is Right for You?</span>
          </h2>
          <p style={{ fontFamily: U, fontSize: 'clamp(14px,1.4vw,16px)', fontWeight: 300, color: C.textMuted, lineHeight: 1.8, margin: '0 auto 40px', maxWidth: 500 }}>
            Our dedicated financial advisors review your situation one-on-one and identify every dollar of funding you qualify for — at no cost to you.
          </p>
          <div className="fp-hero-btns" style={{ justifyContent: 'center' }}>
            <a href="/apply"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: U, fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.white, background: C.navy, padding: '14px 36px', borderRadius: 4, textDecoration: 'none', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = C.navyLight}
              onMouseLeave={e => e.currentTarget.style.background = C.navy}
            >
              <GraduationCap size={15} /> Apply Now
            </a>
            <a href="tel:18884982689"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: U, fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.navy, background: 'transparent', padding: '14px 36px', borderRadius: 4, textDecoration: 'none', border: `1px solid ${C.border}` }}
            >
              <Phone size={15} /> 1-888-498-2689
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
